import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { BsCloudUpload } from "react-icons/bs";
import { FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../../Common/FormComponents";
import { useSession } from "../../../../context/SessionContext";
import { filterStoreProjects } from "../../projectFilters";
import {
  getClientsApi,
  getContractApi,
  saveContractApi,
} from "../../../../Api/ClientWork/ClientWork";
import "../../AddContractorsAndVendors.css";

const CONTRACT_TYPE_OPTIONS = [
  { value: "Lump Sum / Fixed Price", label: "Lump Sum / Fixed Price" },
  { value: "Item Rate / BOQ (disabled)", label: "Item Rate / BOQ (disabled)", disabled: true },
  { value: "Cost-Plus Percentage", label: "Cost-Plus Percentage" },
];

const MOBILIZATION_RECOVERY_OPTIONS = [
  { value: "", label: "Select Recovery Mode" },
  {
    value: "Amortized across first 5 Running Bills (20% each)",
    label: "Amortized across first 5 Running Bills (20% each)",
  },
  {
    value: "Amortized across first 10 Running Bills (10% each)",
    label: "Amortized across first 10 Running Bills (10% each)",
  },
  {
    value: "Deducted equally from all Progressive IPCs",
    label: "Deducted equally from all Progressive IPCs",
  },
  {
    value: "Lump sum adjustment at 50% project completion",
    label: "Lump sum adjustment at 50% project completion",
  },
];

const DLP_OPTIONS = [
  { value: "", label: "Select DLP" },
  { value: "6 Months", label: "6 Months" },
  { value: "12 Months", label: "12 Months" },
  { value: "24 Months", label: "24 Months" },
  { value: "36 Months", label: "36 Months" },
];

/**
 * Client Contract Form Screen
 * Built using the standard ERP FormComponents (InputField, SelectField, TextAreaField, ActionButton, vendor-card)
 */
const ContractForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const guaranteeFileInputRef = useRef(null);

  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [clientsList, setClientsList] = useState([]);

  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  const [formData, setFormData] = useState({
    ContractNo: "",
    ClientId: "",
    ClientName: "",
    ClientNTN: "",
    ContractType: "Lump Sum / Fixed Price",
    ContractDate: todayStr,
    ProjectId: "",
    TargetCompletionDate: "",
    ContractValue: "",
    AdvanceDeposit: "",
    RetentionPercentage: "5.0",
    IncomeTaxWhtRate: "2.0",
    MobilizationRecoveryMode: "Amortized across first 5 Running Bills (20% each)",
    DefectsLiabilityPeriod: "12 Months",
    ScopeOfWork: "",
    OfficialRepresentative: "",
    PrimaryAttachment: "",
    PerformanceGuarantee: "",
    Status: "Active",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const clientsResult = await getClientsApi();
      if (cancelled) {
        return;
      }
      const clients = clientsResult?.data?.IsSuccess === true ? clientsResult.data.Data || [] : [];
      setClientsList(clients);

      if (isEdit) {
        const result = await getContractApi(id);
        if (cancelled) {
          return;
        }
        const match = result?.data?.IsSuccess === true ? result.data.Data : null;
        if (match) {
          setFormData({
            ...match,
            ProjectId: String(match.ProjectId || ""),
            ContractValue: String(match.ContractValue || ""),
            AdvanceDeposit: String(match.AdvanceDeposit || ""),
            RetentionPercentage: String(match.RetentionPercentage || "5.0"),
            IncomeTaxWhtRate: String(match.IncomeTaxWhtRate || "2.0"),
          });
        } else {
          toast.error("Contract record not found");
          history.push("/client-work/contracts");
        }
      } else {
        // the contract number is generated by the server when the contract is saved
        const defaultClient = clients[0];
        setFormData((prev) => ({
          ...prev,
          ClientId: defaultClient ? defaultClient.Id : "",
          ClientName: defaultClient ? defaultClient.OwnerName : "",
          ClientNTN: defaultClient ? defaultClient.NTN : "",
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, history]);

  const { rolesdata } = useSession();
  const projectOptions = [
    { value: "", label: "Select Project" },
    ...filterStoreProjects(rolesdata?.data?.Projects).map((p) => ({
      value: String(p.ProjectId),
      label: p.ProjectName,
    })),
  ];

  const clientOptions = useMemo(() => {
    const list = clientsList.map((c) => ({
      value: String(c.Id),
      label: c.OwnerName,
    }));
    return [{ value: "", label: "Select Project Owner (Client)" }, ...list];
  }, [clientsList]);

  const handleClientChange = (e) => {
    const selectedId = e.target.value;
    const client = clientsList.find((c) => String(c.Id) === String(selectedId));

    if (client) {
      setFormData((prev) => ({
        ...prev,
        ClientId: client.Id,
        ClientName: client.OwnerName,
        ClientNTN: client.NTN || "N/A",
        IncomeTaxWhtRate: client.IncomeTaxWhtRate || prev.IncomeTaxWhtRate,
        RetentionPercentage: client.DefaultRetentionRate || prev.RetentionPercentage,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        ClientId: "",
        ClientName: "",
        ClientNTN: "",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const contractValueNum = useMemo(() => {
    const val = parseFloat(formData.ContractValue) || 0;
    return val;
  }, [formData.ContractValue]);

  const retentionAmountCalculated = useMemo(() => {
    const pct = parseFloat(formData.RetentionPercentage) || 0;
    return contractValueNum * (pct / 100);
  }, [contractValueNum, formData.RetentionPercentage]);

  const whtAmountCalculated = useMemo(() => {
    const rate = parseFloat(formData.IncomeTaxWhtRate) || 0;
    return contractValueNum * (rate / 100);
  }, [contractValueNum, formData.IncomeTaxWhtRate]);

  const handlePrimaryFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, PrimaryAttachment: file.name }));
      toast.success(`Attachment "${file.name}" uploaded successfully`);
    }
  };

  const handleGuaranteeFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        PerformanceGuarantee: file.name,
      }));
      toast.success(`Guarantee document "${file.name}" attached`);
    }
  };

  const handleSaveContract = async (targetStatus = "Active") => {
    if (!formData.ClientName) {
      toast.error("Please select a Project Owner (Client)");
      return;
    }
    if (!formData.ProjectId) {
      toast.error("Please select a Project Name");
      return;
    }
    if (!contractValueNum || contractValueNum <= 0) {
      toast.error("Please enter a valid Contract Value");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        Id: isEdit ? Number(id) : null,
        ...formData,
        ClientId: Number(formData.ClientId),
        ProjectId: Number(formData.ProjectId),
        ContractValue: contractValueNum,
        AdvanceDeposit: parseFloat(formData.AdvanceDeposit) || 0,
        RetentionPercentage: parseFloat(formData.RetentionPercentage) || 0,
        IncomeTaxWhtRate: parseFloat(formData.IncomeTaxWhtRate) || 0,
        Status: targetStatus,
      };

      const result = await saveContractApi(payload);
      if (result?.data?.IsSuccess === true) {
        toast.success(
          isEdit
            ? "Client Contract updated successfully"
            : `Client Contract saved successfully (${targetStatus})`,
        );
        history.push("/client-work/contracts");
      } else {
        toast.error(result?.data?.Message || "Failed to save Client Contract");
      }
    } catch (err) {
      toast.error("Failed to save Client Contract");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="vendor-screen-wrapper app_font">
      {/* ── Page Header ── */}
      <div className="col-md-12 text-left page_heading">
        <div className="d-flex align-items-center">
          <FaArrowLeft
            size={16}
            style={{ cursor: "pointer", marginRight: "12px" }}
            title="Back"
            onClick={() => history.push("/client-work/contracts")}
          />
          <h4 className="m-0">{isEdit ? "Edit" : "Add"} Client Contract</h4>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSaveContract("Active");
        }}
        autoComplete="off"
      >
        {/* ── CARD 1: Contract & Identity Details ── */}
        <div className="vendor-card">
          <h5 className="vendor-card-title">Contract & Identity Details</h5>

          <div className="row mb-2">
            <InputField
              label="Contract / Agreement No:"
              name="ContractNo"
              disabled
              value={formData.ContractNo}
              onChange={handleInputChange}
              placeholder="Auto-generated on save"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <SelectField
              label="Project Owner (Client):"
              name="ClientId"
              value={formData.ClientId}
              onChange={handleClientChange}
              options={clientOptions}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            >
              <small
                style={{
                  fontSize: "11px",
                  color: formData.ClientNTN ? "#056363" : "#64748b",
                  fontWeight: 500,
                  display: "block",
                }}
              >
                {formData.ClientNTN
                  ? `Verified NTN: ${formData.ClientNTN}`
                  : "Select client to view NTN"}
              </small>
            </SelectField>

            <SelectField
              label="Contract Type:"
              name="ContractType"
              value={formData.ContractType}
              onChange={handleInputChange}
              options={CONTRACT_TYPE_OPTIONS}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            >
              <small className="text-muted" style={{ fontSize: "11px", display: "block" }}>
                BOQ Table is disabled for this rollout
              </small>
            </SelectField>

            <InputField
              label="Contract Date:"
              name="ContractDate"
              type="date"
              value={formData.ContractDate}
              onChange={handleInputChange}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <SelectField
              label="Project Name:"
              name="ProjectId"
              value={formData.ProjectId}
              onChange={handleInputChange}
              options={projectOptions}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <InputField
              label="Target Completion Date:"
              name="TargetCompletionDate"
              type="date"
              value={formData.TargetCompletionDate}
              onChange={handleInputChange}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
          </div>
        </div>

        {/* ── CARD 2: Commercial & Financial Terms ── */}
        <div className="vendor-card">
          <h5 className="vendor-card-title">Commercial & Financial Terms</h5>

          <div className="row mb-2">
            <InputField
              label="Contract Value:"
              name="ContractValue"
              type="number"
              value={formData.ContractValue}
              onChange={handleInputChange}
              placeholder="185000000"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <InputField
              label="Advance / Mobilization Deposit:"
              name="AdvanceDeposit"
              type="number"
              value={formData.AdvanceDeposit}
              onChange={handleInputChange}
              placeholder="18500000"
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <InputField
              label="Retention Money Withheld (%):"
              name="RetentionPercentage"
              type="number"
              step="0.1"
              value={formData.RetentionPercentage}
              onChange={handleInputChange}
              placeholder="5.0"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            >
              <small
                style={{ fontSize: "11px", color: "#056363", fontWeight: 500, display: "block" }}
              >
                Dynamic calculation: PKR {retentionAmountCalculated.toLocaleString()}
              </small>
            </InputField>

            <InputField
              label="Income Tax WHT Rate (%):"
              name="IncomeTaxWhtRate"
              type="number"
              step="0.1"
              value={formData.IncomeTaxWhtRate}
              onChange={handleInputChange}
              placeholder="2.0"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            >
              <small
                style={{ fontSize: "11px", color: "#056363", fontWeight: 500, display: "block" }}
              >
                PKR {whtAmountCalculated.toLocaleString()}
              </small>
            </InputField>

            <SelectField
              label="Mobilization Recovery Mode:"
              name="MobilizationRecoveryMode"
              value={formData.MobilizationRecoveryMode}
              onChange={handleInputChange}
              options={MOBILIZATION_RECOVERY_OPTIONS}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <SelectField
              label="Defects Liability Period:"
              name="DefectsLiabilityPeriod"
              value={formData.DefectsLiabilityPeriod}
              onChange={handleInputChange}
              options={DLP_OPTIONS}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
          </div>
        </div>

        {/* ── CARD 3: Work Scope & Document Attachments ── */}
        <div className="vendor-card">
          <h5 className="vendor-card-title">Work Scope & Document Attachments</h5>

          <div className="row mb-2">
            <InputField
              label="Official Representative:"
              name="OfficialRepresentative"
              value={formData.OfficialRepresentative}
              onChange={handleInputChange}
              placeholder="e.g. Engr. Faisal (Project Manager)"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <TextAreaField
              label="Description:"
              name="ScopeOfWork"
              value={formData.ScopeOfWork}
              onChange={handleInputChange}
              placeholder="Construction of Grey Structure (Basement + Ground + 5 Floors)..."
              rows={2}
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
              <label className="input_label d-block m-0 mb-2">
                Primary Attachment / Agreement:
                <span className="estaric text-danger ml-1">*</span>
              </label>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                onChange={handlePrimaryFileUpload}
                accept=".pdf,.docx,.doc"
              />
              {formData.PrimaryAttachment ? (
                <div className="document-upload-box justify-content-start">
                  <BsCloudUpload className="document-upload-icon" />
                  <div className="document-upload-file">
                    <span title={formData.PrimaryAttachment}>{formData.PrimaryAttachment}</span>
                    <button
                      type="button"
                      className="document-upload-remove"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, PrimaryAttachment: "" }));
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      title="Remove attachment"
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="document-upload-box"
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  <BsCloudUpload className="document-upload-icon" />
                  <div className="document-upload-copy">
                    <strong>Upload signed agreement</strong>
                    <span>PDF, DOC or DOCX</span>
                  </div>
                </div>
              )}
            </div>

            <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
              <label className="input_label d-block m-0 mb-2">Performance Guarantee:</label>
              <input
                type="file"
                ref={guaranteeFileInputRef}
                hidden
                onChange={handleGuaranteeFileUpload}
                accept=".pdf,.docx,.doc,.png,.jpg,.jpeg"
              />
              {formData.PerformanceGuarantee ? (
                <div className="document-upload-box justify-content-start">
                  <BsCloudUpload className="document-upload-icon" />
                  <div className="document-upload-file">
                    <span title={formData.PerformanceGuarantee}>
                      {formData.PerformanceGuarantee}
                    </span>
                    <button
                      type="button"
                      className="document-upload-remove"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, PerformanceGuarantee: "" }));
                        if (guaranteeFileInputRef.current) {
                          guaranteeFileInputRef.current.value = "";
                        }
                      }}
                      title="Remove guarantee"
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="document-upload-box"
                  role="button"
                  tabIndex={0}
                  onClick={() => guaranteeFileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      guaranteeFileInputRef.current?.click();
                    }
                  }}
                >
                  <BsCloudUpload className="document-upload-icon" />
                  <div className="document-upload-copy">
                    <strong>Upload guarantee document</strong>
                    <span>PDF, image, DOC or DOCX</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Bottom Actions Bar ── */}
        <div className="col-12 p-0">
          <div className="float-right mt-2 mb-4 d-flex align-items-center" style={{ gap: "10px" }}>
            <ActionButton
              text="Cancel"
              variant="cancel"
              type="button"
              onClick={() => history.push("/client-work/contracts")}
              disabled={isLoading}
            />
            <ActionButton
              text="Save as Draft"
              variant="cancel"
              type="button"
              onClick={() => handleSaveContract("Draft")}
              disabled={isLoading}
            />
            <ActionButton
              text={isEdit ? "Update & Active" : "Save & Active"}
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContractForm;
